import type {
  CategoriaComMotivos,
  CategoriaMotivoForm,
  CategoriaMotivoResumo,
  ConviteEmitido,
  FiltroAuditoria,
  MensagemRapidaForm,
  MensagemRapidaResumo,
  PaginaAuditoria,
  PerfilForm,
  PerfilResumo,
  SetorDetalhe,
  SetorForm,
  SetorResumo,
  NovoTecnico,
  PoliticaPrivacidade,
  PoliticaPrivacidadeForm,
  TagForm,
  TecnicoForm,
  TecnicoResumo,
  TagResumo,
  UsuarioForm,
  UsuarioResumo,
  ConfirmarImportacaoTecnicos,
  ConfirmarImportacaoUsuarios,
  PreviaImportacao,
  ResultadoImportacao,
} from '@mymaxx/shared';
import { api, baixarArquivo } from './api';

const base = '/v1/gestao';

/** Monta a query string ignorando campos vazios. */
function query(params: Record<string, string | number | undefined>): string {
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(params)) {
    if (valor !== undefined && valor !== '') busca.set(chave, String(valor));
  }
  const texto = busca.toString();
  return texto ? `?${texto}` : '';
}

export const gestaoApi = {
  // setores
  listarSetores: () => api.get<SetorResumo[]>(`${base}/setores`),
  detalheSetor: (id: string) => api.get<SetorDetalhe>(`${base}/setores/${id}`),
  criarSetor: (dados: SetorForm) => api.post<SetorDetalhe>(`${base}/setores`, dados),
  atualizarSetor: (id: string, dados: SetorForm) =>
    api.put<SetorDetalhe>(`${base}/setores/${id}`, dados),
  desativarSetor: (id: string) => api.delete<SetorDetalhe>(`${base}/setores/${id}`),

  // usuários
  listarUsuarios: (busca?: string) =>
    api.get<UsuarioResumo[]>(`${base}/usuarios${query({ busca })}`),
  criarUsuario: (dados: UsuarioForm) => api.post<ConviteEmitido>(`${base}/usuarios`, dados),
  atualizarUsuario: (id: string, dados: UsuarioForm) =>
    api.put<UsuarioResumo>(`${base}/usuarios/${id}`, dados),
  excluirUsuario: (id: string) => api.delete<void>(`${base}/usuarios/${id}`),
  reenviarConvite: (id: string) => api.post<ConviteEmitido>(`${base}/usuarios/${id}/convite`),
  redefinirTotp: (id: string) => api.post<UsuarioResumo>(`${base}/usuarios/${id}/totp/redefinir`),

  // perfis
  listarPerfis: () => api.get<PerfilResumo[]>(`${base}/perfis`),
  criarPerfil: (dados: PerfilForm) => api.post<PerfilResumo>(`${base}/perfis`, dados),
  atualizarPerfil: (id: string, dados: PerfilForm) =>
    api.put<PerfilResumo>(`${base}/perfis/${id}`, dados),
  excluirPerfil: (id: string) => api.delete<void>(`${base}/perfis/${id}`),

  // catálogos — categorias (nível 1)
  listarCategorias: () => api.get<CategoriaMotivoResumo[]>(`${base}/categorias-motivo`),
  criarCategoria: (dados: CategoriaMotivoForm) =>
    api.post<CategoriaMotivoResumo>(`${base}/categorias-motivo`, dados),
  atualizarCategoria: (id: string, dados: CategoriaMotivoForm) =>
    api.put<CategoriaMotivoResumo>(`${base}/categorias-motivo/${id}`, dados),
  removerCategoria: (id: string) => api.delete<void>(`${base}/categorias-motivo/${id}`),

  // catálogos — motivos (nível 2)
  listarMotivos: () => api.get<CategoriaComMotivos[]>(`${base}/motivos`),
  listarTags: () => api.get<TagResumo[]>(`${base}/tags`),
  // aviso de privacidade
  lerPolitica: () => api.get<PoliticaPrivacidade>(`${base}/politica-privacidade`),
  salvarPolitica: (dados: PoliticaPrivacidadeForm) =>
    api.put<PoliticaPrivacidade>(`${base}/politica-privacidade`, dados),

  // técnicos de liberação
  listarTecnicos: () => api.get<TecnicoResumo[]>(`${base}/tecnicos`),
  criarTecnico: (dados: NovoTecnico) => api.post<TecnicoResumo>(`${base}/tecnicos`, dados),
  atualizarTecnico: (id: string, dados: TecnicoForm) =>
    api.put<TecnicoResumo>(`${base}/tecnicos/${id}`, dados),
  trocarSenhaTecnico: (id: string, senha: string) =>
    api.post<void>(`${base}/tecnicos/${id}/senha`, { senha }),

  criarTag: (dados: TagForm) => api.post<TagResumo>(`${base}/tags`, dados),
  atualizarTag: (id: string, dados: TagForm) => api.put<TagResumo>(`${base}/tags/${id}`, dados),
  removerTag: (id: string) => api.delete<void>(`${base}/tags/${id}`),

  listarMensagens: () => api.get<MensagemRapidaResumo[]>(`${base}/mensagens-rapidas`),
  criarMensagem: (dados: MensagemRapidaForm) =>
    api.post<MensagemRapidaResumo>(`${base}/mensagens-rapidas`, dados),
  atualizarMensagem: (id: string, dados: MensagemRapidaForm) =>
    api.put<MensagemRapidaResumo>(`${base}/mensagens-rapidas/${id}`, dados),
  removerMensagem: (id: string) => api.delete<void>(`${base}/mensagens-rapidas/${id}`),

  // auditoria
  consultarAuditoria: (filtro: Partial<FiltroAuditoria>) =>
    api.get<PaginaAuditoria>(
      `${base}/auditoria${query({
        acao: filtro.acao,
        usuarioId: filtro.usuarioId,
        de: filtro.de,
        ate: filtro.ate,
        antesDe: filtro.antesDe,
        limite: filtro.limite,
      })}`,
    ),
  acoesAuditoria: () => api.get<string[]>(`${base}/auditoria/acoes`),

  // importação por planilha
  /** Passo 1: manda a planilha e recebe a prévia. Nada é criado aqui. */
  analisarImportacao: (tipo: 'usuarios' | 'tecnicos', arquivo: File) =>
    api.post<PreviaImportacao>(
      `${base}/importacoes/${tipo}/analisar?arquivo=${encodeURIComponent(arquivo.name)}`,
      arquivo,
    ),
  confirmarImportacaoUsuarios: (dados: ConfirmarImportacaoUsuarios) =>
    api.post<ResultadoImportacao>(`${base}/importacoes/usuarios/confirmar`, dados),
  confirmarImportacaoTecnicos: (dados: ConfirmarImportacaoTecnicos) =>
    api.post<ResultadoImportacao>(`${base}/importacoes/tecnicos/confirmar`, dados),
  baixarModeloImportacao: (tipo: 'usuarios' | 'tecnicos') =>
    baixarArquivo(`${base}/importacoes/${tipo}/modelo`, `modelo-${tipo}.xlsx`),
};

/** Rotas públicas do convite (o usuário ainda não tem senha). */
export const conviteApi = {
  validar: (token: string) =>
    api.get<{ nome: string; email: string; exigeTotp: boolean }>(
      `/v1/convite?token=${encodeURIComponent(token)}`,
      { autenticado: false },
    ),
  definirSenha: (token: string, senha: string, confirmacao: string) =>
    api.post<void>('/v1/convite/senha', { token, senha, confirmacao }, { autenticado: false }),
};
